import { AnalyticsCard } from '@/components/analytics/analyticsCard'
import { BenchmarkApyComparison } from '@/components/analytics/benchmarkApyComparison'
import { BenchmarkApyComparisonFullView } from '@/components/analytics/benchmarkApyComparisonFullView'
import { AnalyticsBenchmarks } from '@/components/analytics/benchmarks'
import { BenchmarksFullView } from '@/components/analytics/benchmarksFullView'
import { BestPerformingVaults } from '@/components/analytics/bestPerformingVaults'
import { BestPerformingVaultsFullView } from '@/components/analytics/bestPerformingVaultsFullView'
import { TvlFullView } from '@/components/analytics/tvlFullView'
import { TvlSection } from '@/components/analytics/tvlSection'
import type { BenchmarkItem } from '@/components/analytics/vaultSelector'
import { Breadcrumb } from '@/components/common/breadcrumbs'
import { RadioGroup, RadioTile } from '@/components/common/radio'
import { ScrollArea, ScrollBar } from '@/components/common/scrollArea'
import { Content, Page } from '@/components/layout/wrappers'
import { ApyRangeSettingsSelector } from '@/components/shared/apyRangeSettingsSelector'
import { ChartRangeSelector } from '@/components/shared/chartRangeSelector'
import { queryClient } from '@/config/query/queryClient'
import { APP_META_DESCRIPTION } from '@/constants/meta'
import { VALID_CHART_PERIODS, useSettingsContext } from '@/context/settings'
import { allNetworksBenchmarksQueryOptions } from '@/queries/allNetworksBenchmarks.query'
import { historicalBenchmarkQueryOptions, useHistoricalBenchmarkQuery } from '@/queries/historicalBenchmarks.query'
import { vaultsQueryOptions } from '@/queries/vaults.query'
import type { BenchmarkGroup } from '@/types/analytics'
import type { ChartPeriod } from '@/types/periods'
import type { Vault } from '@/types/vault'
import { getMetaTags } from '@/utils/meta/getMetaTags'
import { YEAR_IN_DAYS } from '@vaultsfyi/common/constants'
import { getHistoricalBenchmarksWithCache } from 'lib/historical-benchmarks-cache.server'
import { useState } from 'react'
import type { MetaFunction } from 'react-router'
import { useLoaderData } from 'react-router'

export const loader = async () => {
  const [ethResult, usdResult, vaultsResult] = await Promise.allSettled([
    getHistoricalBenchmarksWithCache('eth'),
    getHistoricalBenchmarksWithCache('usd'),
    queryClient.ensureQueryData(vaultsQueryOptions()),
  ])

  const benchmarkPayload = (data: Awaited<ReturnType<typeof getHistoricalBenchmarksWithCache>>) => ({
    data,
    success: true,
    loadTime: 0,
    fromCache: true,
  })

  if (usdResult.status === 'fulfilled') {
    queryClient.setQueryData(allNetworksBenchmarksQueryOptions('usd').queryKey, benchmarkPayload(usdResult.value))
    queryClient.setQueryData(
      historicalBenchmarkQueryOptions({ code: 'usd', network: 'mainnet' }).queryKey,
      benchmarkPayload(usdResult.value)
    )
  }
  if (ethResult.status === 'fulfilled') {
    queryClient.setQueryData(allNetworksBenchmarksQueryOptions('eth').queryKey, benchmarkPayload(ethResult.value))
    queryClient.setQueryData(
      historicalBenchmarkQueryOptions({ code: 'eth', network: 'mainnet' }).queryKey,
      benchmarkPayload(ethResult.value)
    )
  }

  return {
    initialEthBenchmarks:
      ethResult.status === 'fulfilled'
        ? { data: ethResult.value, success: true, loadTime: 0, fromCache: true }
        : undefined,
    initialUsdBenchmarks:
      usdResult.status === 'fulfilled'
        ? { data: usdResult.value, success: true, loadTime: 0, fromCache: true }
        : undefined,
    initialVaults: vaultsResult.status === 'fulfilled' ? vaultsResult.value : undefined,
  }
}

export type AnalyticsLoader = typeof loader

export const meta: MetaFunction = ({ location }) => {
  return getMetaTags({
    title: 'Analytics',
    description: APP_META_DESCRIPTION,
    url: location.pathname,
  })
}

export default function Analytics() {
  const { initialEthBenchmarks, initialUsdBenchmarks, initialVaults } = useLoaderData<AnalyticsLoader>()
  const { apyRange, includeRewards } = useSettingsContext()
  const [chartsPeriod, setChartsPeriod] = useState<ChartPeriod>(YEAR_IN_DAYS)
  const [selectedBenchmarkNetworks, setSelectedBenchmarkNetworks] = useState<Set<string>>(new Set())
  const [selectedComparisonVaults, setSelectedComparisonVaults] = useState<Vault[]>([])
  const [selectedComparisonBenchmarks, setSelectedComparisonBenchmarks] = useState<BenchmarkItem[]>([])
  const [selectedBestPerformingGroup, setSelectedBestPerformingGroup] = useState<BenchmarkGroup>('usd')

  const { benchmark: ethBenchmarkData } = useHistoricalBenchmarkQuery({
    code: 'eth',
    network: 'mainnet',
    initialData: initialEthBenchmarks,
  })
  const { benchmark: usdBenchmarkData } = useHistoricalBenchmarkQuery({
    code: 'usd',
    network: 'mainnet',
    initialData: initialUsdBenchmarks,
  })

  return (
    <Page className="max-md:bg-page">
      <Breadcrumb aria-label="settings" className="py-1">
        <div className="flex w-full flex-wrap items-center justify-end gap-x-4 gap-y-2">
          <div className="flex w-fit flex-wrap items-center justify-end gap-1">
            <p className="text-sm">Show APY as avg. of</p>
            <ApyRangeSettingsSelector className="h-6 bg-page p-0.5" />
          </div>
          <div className="flex w-fit flex-wrap items-center justify-end gap-1">
            <p className="text-sm">Charts period</p>
            <ChartRangeSelector
              prefix="last"
              chartPeriods={VALID_CHART_PERIODS.filter((period) => period !== 7)}
              chartsPeriod={chartsPeriod}
              onRangeSelect={setChartsPeriod}
              className="h-6 rounded-full px-1 pl-2"
            />
          </div>
        </div>
      </Breadcrumb>
      <ScrollArea className="h-full">
        <Content>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <AnalyticsCard
              id="tvl"
              title="Total Value Locked"
              description="How much capital is currently deposited across all vaults"
              footerLabel="See a full breakdown at once"
              fullView={<TvlFullView initialData={initialVaults} />}
              dialogClassName="rounded-[32px]"
            >
              <TvlSection initialData={initialVaults} />
            </AnalyticsCard>
            <AnalyticsCard
              id="benchmark-rates"
              title="Vaults.fyi benchmark rates"
              description="Measures average APY across largest markets on each network"
              footerLabel="Compare with different networks"
              fullView={
                <BenchmarksFullView
                  apyRange={apyRange}
                  includeRewards={includeRewards}
                  chartPeriod={chartsPeriod}
                  selectedNetworks={selectedBenchmarkNetworks}
                  onSelectedNetworksChange={setSelectedBenchmarkNetworks}
                />
              }
              dialogClassName="rounded-[32px]"
            >
              <AnalyticsBenchmarks
                apyRange={apyRange}
                includeRewards={includeRewards}
                chartPeriod={chartsPeriod}
                selectedNetworks={selectedBenchmarkNetworks}
                onSelectedNetworksChange={setSelectedBenchmarkNetworks}
              />
            </AnalyticsCard>
            <AnalyticsCard
              id="benchmark-comparison"
              title="Benchmark APY Comparison"
              description="Compare the APY of any vault over time against benchmark rates"
              footerLabel="Compare with more vaults and benchmarks"
              fullView={
                <BenchmarkApyComparisonFullView
                  apyRange={apyRange}
                  includeRewards={includeRewards}
                  chartPeriod={chartsPeriod}
                  ethBenchmarkData={ethBenchmarkData}
                  usdBenchmarkData={usdBenchmarkData}
                  selectedVaults={selectedComparisonVaults}
                  onVaultsChange={setSelectedComparisonVaults}
                  selectedBenchmarks={selectedComparisonBenchmarks}
                  onBenchmarksChange={setSelectedComparisonBenchmarks}
                />
              }
              dialogClassName="rounded-[32px]"
            >
              <BenchmarkApyComparison
                apyRange={apyRange}
                includeRewards={includeRewards}
                chartPeriod={chartsPeriod}
                ethBenchmarkData={ethBenchmarkData}
                usdBenchmarkData={usdBenchmarkData}
                selectedVaults={selectedComparisonVaults}
                onVaultsChange={setSelectedComparisonVaults}
                selectedBenchmarks={selectedComparisonBenchmarks}
                onBenchmarksChange={setSelectedComparisonBenchmarks}
              />
            </AnalyticsCard>
            <AnalyticsCard
              id="best-performing-vaults"
              title="Best performing vaults ($10M+ TVL)"
              description={`Change in share price over last ${chartsPeriod} days`}
              footerLabel="See full rankings"
              actions={
                <RadioGroup
                  value={selectedBestPerformingGroup}
                  onValueChange={(v) => setSelectedBestPerformingGroup(v as BenchmarkGroup)}
                  className="h-6 grid-cols-2 bg-page p-0.5"
                >
                  <RadioTile id="bpv-usd" value="usd">
                    USD
                  </RadioTile>
                  <RadioTile id="bpv-eth" value="eth">
                    ETH
                  </RadioTile>
                </RadioGroup>
              }
              fullView={
                <BestPerformingVaultsFullView
                  chartPeriod={chartsPeriod}
                  group={selectedBestPerformingGroup}
                  onGroupChange={setSelectedBestPerformingGroup}
                />
              }
              dialogClassName="rounded-[32px]"
            >
              <BestPerformingVaults chartPeriod={chartsPeriod} group={selectedBestPerformingGroup} />
            </AnalyticsCard>
          </div>
        </Content>
        <ScrollBar orientation="vertical" />
      </ScrollArea>
    </Page>
  )
}
